import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, List, message, Upload, Space, Typography, Empty, Spin, Popconfirm } from 'antd';
import { UploadOutlined, DeleteOutlined, FileTextOutlined } from '@ant-design/icons';
import apiClient from '../api';

const KnowledgeBaseManager = ({ templateId, onUpdate }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const loadFiles = useCallback(async () => {
    if (!templateId) return;
    setLoading(true);
    try {
      const res = await apiClient.get(`/knowledge/files?templateId=${templateId}`);
      if (res.data.success) setFiles(res.data.files || []);
    } catch (err) {
      message.error('Failed to load knowledge base files');
      setFiles([]);
    } finally {
      setLoading(false);
    }
  }, [templateId]);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  const handleUpload = async (file) => {
    if (!templateId) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('templateId', templateId);
      const res = await apiClient.post(
        '/knowledge/upload',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      if (res.data.success) {
        message.success(`"${file.name}" added to knowledge base`);
        loadFiles();
        if (onUpdate) onUpdate();
      } else {
        message.error(res.data.message || 'Upload failed');
      }
    } catch (err) {
      message.error(err.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
    return false; // prevent default upload
  };

  const handleDelete = async (fileId, originalName) => {
    if (!templateId || !fileId) return;
    try {
      const res = await apiClient.delete(
        `/knowledge/files/${encodeURIComponent(fileId)}`
      );
      if (res.data.success) {
        message.success(`"${originalName}" removed`);
        loadFiles();
        if (onUpdate) onUpdate();
      } else {
        message.error(res.data.message || 'Delete failed');
      }
    } catch (err) {
      message.error(err.response?.data?.message || 'Delete failed');
    }
  };

  if (!templateId) return null;

  return (
    <Card
      title={
        <Space>
          <FileTextOutlined />
          <span>Knowledge Base (RAG)</span>
        </Space>
      }
      extra={
        <Upload
          accept=".txt,.pdf,.md,.csv,.html,.htm,.json,.docx"
          showUploadList={false}
          beforeUpload={handleUpload}
          disabled={uploading}
        >
          <Button type="primary" icon={<UploadOutlined />} loading={uploading}>
            Upload file
          </Button>
        </Upload>
      }
      style={{ marginTop: 16 }}
    >
      <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
        Upload documents: .txt, .pdf, .md, .csv, .html, .json, .docx. The bot uses this content to answer (RAG). Set OPENAI_API_KEY for semantic search.
      </Typography.Paragraph>
      {loading ? (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin />
        </div>
      ) : files.length === 0 ? (
        <Empty
          description="No files yet. Upload .txt, .pdf, .md, .csv, .html, .docx to build the knowledge base."
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      ) : (
        <List
          dataSource={files}
          renderItem={(item) => (
            <List.Item
              actions={[
                <Popconfirm
                  key="del"
                  title="Remove this file from the knowledge base?"
                  onConfirm={() => handleDelete(item._id, item.originalName)}
                >
                  <Button type="text" danger size="small" icon={<DeleteOutlined />}>
                    Remove
                  </Button>
                </Popconfirm>,
              ]}
            >
              <List.Item.Meta
                avatar={<FileTextOutlined style={{ fontSize: 20, color: '#1890ff' }} />}
                title={item.originalName}
                description={`${item.chunkCount || 0} chunks · ${item.size ? Math.round(item.size / 1024) + ' KB' : ''}`}
              />
            </List.Item>
          )}
        />
      )}
    </Card>
  );
};

export default KnowledgeBaseManager;
